import React from 'react';
import type {ValidateResult} from 'react-hook-form';
import type {FieldValues, UseControllerProps} from './types';

type InputValidationFunction = (
  value: unknown,
  formValues: FieldValues,
) =>
  | ValidateResult
  | Promise<ValidateResult>
  | React.ReactNode
  | Promise<React.ReactNode>;

type WrappedValidationFunction = (
  value: unknown,
  formValues: FieldValues,
) => ValidateResult | Promise<ValidateResult>;

/**
 * Hook to handle ReactNode validation errors from intl.formatMessage().
 * Wraps validation functions to intercept ReactNode returns and store them,
 * while returning a placeholder string to react-hook-form. The real ReactNode
 * is extracted when displaying the error.
 */
export const useWrappedFormValidation = (
  rules: UseControllerProps['rules'],
) => {
  const reactNodeErrorRef = React.useRef<React.ReactNode | null>(null);

  const wrappedRules = React.useMemo(() => {
    if (!rules?.validate) {
      return rules;
    }

    const wrapValidateFn = (
      fn: InputValidationFunction,
    ): WrappedValidationFunction => {
      return async (value: unknown, formValues: FieldValues) => {
        const result = await fn(value, formValues);

        // React-hook-form only accepts string errors, not ReactNodes. When validation
        // returns a ReactNode (e.g., from intl.formatMessage), we store it in a ref
        // and return a placeholder string to signal validation failure. The placeholder
        // is never shown to users as extractError() retrieves the real ReactNode.
        if (React.isValidElement(result)) {
          reactNodeErrorRef.current = result;
          return 'Error';
        }

        // Clear stored error if validation passed
        if (result === undefined || result === true) {
          reactNodeErrorRef.current = null;
        }

        return result as ValidateResult;
      };
    };

    const validate =
      typeof rules.validate === 'function'
        ? wrapValidateFn(rules.validate)
        : Object.fromEntries(
            Object.entries(rules.validate).map(([key, fn]) => [
              key,
              wrapValidateFn(fn),
            ]),
          );

    return {...rules, validate};
  }, [rules]);

  const extractError = React.useCallback(
    (errorMessage: string | undefined): React.ReactNode | undefined => {
      // Always prefer ReactNode from ref if it exists
      return reactNodeErrorRef.current ?? errorMessage;
    },
    [],
  );

  return {wrappedRules, extractError};
};
